import type { IsoWeekday, ReportEmailDeliveryFrequency } from "../../client-messages";

export type ReportEmailDeliveryFrequencyName = ReportEmailDeliveryFrequency["frequency"];

export interface ReportEmailDeliveryFormState {
  /** What the form started from; `isDirty` compares against it. */
  readonly initial: ReportEmailDeliveryFrequency;
  readonly frequency: ReportEmailDeliveryFrequencyName;
  /** Kept while daily, so switching back to weekly restores it. */
  readonly weekday: IsoWeekday;
}

/** New deliveries default to weekly on Monday (decided 2026-10-16). */
const NEW_DELIVERY_WEEKDAY: IsoWeekday = 1;
const NEW_DELIVERY: ReportEmailDeliveryFrequency = { frequency: "weekly", weekday: NEW_DELIVERY_WEEKDAY };

export function initialReportEmailDeliveryForm(
  initial: ReportEmailDeliveryFrequency = NEW_DELIVERY,
): ReportEmailDeliveryFormState {
  return {
    initial,
    frequency: initial.frequency,
    weekday: initial.frequency === "weekly" ? initial.weekday : NEW_DELIVERY_WEEKDAY,
  };
}

export type ReportEmailDeliveryFormAction =
  | { readonly type: "setFrequency"; readonly frequency: ReportEmailDeliveryFrequencyName }
  | { readonly type: "setWeekday"; readonly weekday: IsoWeekday }
  /** Starts the form over from `initial` (a new delivery when omitted), e.g. after a save or a stop. */
  | { readonly type: "reseed"; readonly initial?: ReportEmailDeliveryFrequency | undefined };

export function reportEmailDeliveryFormReducer(
  state: ReportEmailDeliveryFormState,
  action: ReportEmailDeliveryFormAction,
): ReportEmailDeliveryFormState {
  switch (action.type) {
    case "setFrequency":
      return { ...state, frequency: action.frequency };
    case "setWeekday":
      return { ...state, weekday: action.weekday };
    case "reseed":
      return initialReportEmailDeliveryForm(action.initial);
  }
}

export function reportEmailDeliveryFormValue(state: ReportEmailDeliveryFormState): ReportEmailDeliveryFrequency {
  return state.frequency === "daily" ? { frequency: "daily" } : { frequency: "weekly", weekday: state.weekday };
}

export function isReportEmailDeliveryFormDirty(state: ReportEmailDeliveryFormState): boolean {
  return !isSameReportEmailDeliveryFrequency(reportEmailDeliveryFormValue(state), state.initial);
}

/** Whether the form was seeded from `initial` (a new delivery when omitted). */
export function isReportEmailDeliveryFormSeededFrom(
  state: ReportEmailDeliveryFormState,
  initial: ReportEmailDeliveryFrequency = NEW_DELIVERY,
): boolean {
  return isSameReportEmailDeliveryFrequency(state.initial, initial);
}

function isSameReportEmailDeliveryFrequency(a: ReportEmailDeliveryFrequency, b: ReportEmailDeliveryFrequency): boolean {
  return a.frequency === "weekly" && b.frequency === "weekly" ? a.weekday === b.weekday : a.frequency === b.frequency;
}
